/**
 * Building transfer — pure subtree helpers.
 *
 * Side-effect-free functions shared by export and import: buildingKey
 * validation, thumbnail-derivative detection, referenced-UID collection,
 * name-only user projection, and export sanitization. Kept pure so they are
 * unit-tested without Firebase.
 *
 * Originally ported from the mf-functions `feat/building-transfer` branch; that
 * functions mirror no longer exists, this file is the only implementation.
 */
import {
    EXCLUDED_BUILDING_SUBTREES,
    UID_SCALAR_FIELDS,
    ExportedUsers,
    bundleStoragePrefixes,
} from './bundleFormat';

/** Module-scoped lookup set of UID-bearing field names (hoisted, not rebuilt per call). */
const UID_FIELD_SET: ReadonlySet<string> = new Set(UID_SCALAR_FIELDS);

/**
 * Validate a building key before it touches DB refs or Storage prefixes.
 * Building keys are Firebase push IDs with no special characters, so anything
 * that path-traversal or RTDB-illegal characters would alter is rejected
 * rather than silently sanitized.
 *
 * @throws Error on invalid input.
 */
export function validateBuildingKey(raw: unknown): string {
    if (typeof raw !== 'string') {
        throw new Error('buildingKey must be a string');
    }
    const key = raw.trim();
    if (key.length === 0) {
        throw new Error('buildingKey is required');
    }
    if (key.includes('..') || key.includes('/')) {
        throw new Error('buildingKey contains an illegal path segment');
    }
    // RTDB-illegal characters: . # $ [ ]
    if (/[.#$[\]]/.test(key)) {
        throw new Error('buildingKey contains an illegal character');
    }
    return key;
}

/**
 * True when a Storage object name is a generated thumbnail/scaled derivative
 * (mirrors the skip check in mf-functions images/thumbnails.ts). The export
 * skips derivatives entirely — the target instance's thumbnail trigger
 * regenerates them, since the import writes DB metadata before bytes.
 */
export function isThumbnailDerivativePath(objectName: string): boolean {
    return /_\d+x\d+/.test(objectName) || /_scaled_\d+/.test(objectName);
}

/**
 * Walk an arbitrary DB subtree value and collect every user UID referenced by a
 * field named in UID_SCALAR_FIELDS. Null/empty values are ignored (e.g.
 * ValueWithMetadata.updatedBy may be null). Order-stable, de-duplicated.
 */
export function collectReferencedUids(...roots: unknown[]): string[] {
    const uids = new Set<string>();

    const visit = (value: unknown): void => {
        if (value === null || typeof value !== 'object') {
            return;
        }
        if (Array.isArray(value)) {
            for (const item of value) visit(item);
            return;
        }
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
            if (UID_FIELD_SET.has(key) && typeof child === 'string' && child.length > 0) {
                uids.add(child);
            }
            visit(child);
        }
    };

    for (const root of roots) visit(root);
    return [...uids];
}

/**
 * Project the full user list down to NAME-ONLY records for the referenced UIDs.
 * Never carries email, privileges, or any other PII into the export.
 * UIDs absent from the source list are skipped.
 */
export function projectUsersNameOnly(
    allUsers: Record<string, { name?: string } | null | undefined> | null | undefined,
    referencedUids: string[]
): ExportedUsers {
    const out: ExportedUsers = {};
    if (!allUsers) return out;
    for (const uid of referencedUids) {
        const user = allUsers[uid];
        if (user && typeof user.name === 'string') {
            out[uid] = { name: user.name };
        }
    }
    return out;
}

/**
 * Deep-clone a value while removing derived URL fields — any property whose key
 * ends in "url"/"urls" (case-insensitive). Drops environment-specific cached
 * cover/thumbnail URLs (PhotoUrlData) before export; `storagePath`, dimensions,
 * and keys are preserved as the source of truth. The target environment
 * re-resolves URLs from `storagePath`.
 */
export function stripDerivedUrls<T>(value: T): T {
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(item => stripDerivedUrls(item)) as unknown as T;
    }
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
        if (/urls?$/i.test(key)) {
            continue;
        }
        out[key] = stripDerivedUrls(child);
    }
    return out as T;
}

/** Deep-clone a value while removing every property whose key is in `names`. */
export function stripKeysDeep<T>(value: T, names: ReadonlySet<string>): T {
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(item => stripKeysDeep(item, names)) as unknown as T;
    }
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
        if (names.has(key)) {
            continue;
        }
        out[key] = stripKeysDeep(child, names);
    }
    return out as T;
}

const EXCLUDED_SUBTREE_SET: ReadonlySet<string> = new Set(EXCLUDED_BUILDING_SUBTREES);

/** Environment/session state that must not travel: derived thumbnail flags and the offline lock. */
const STRIPPED_STATE_KEYS: ReadonlySet<string> = new Set(['thumbnailsGenerated', 'offlineLock']);

/**
 * Sanitize the `buildings/{key}` node for export: drop excluded subtrees
 * (timelineItems, todos, externalContacts), derived URL caches, and
 * environment-specific state flags.
 */
export function sanitizeBuildingForExport(building: unknown): unknown {
    if (building === null || typeof building !== 'object' || Array.isArray(building)) {
        return building;
    }
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(building as Record<string, unknown>)) {
        if (EXCLUDED_SUBTREE_SET.has(key)) {
            continue;
        }
        out[key] = child;
    }
    return stripKeysDeep(stripDerivedUrls(out), STRIPPED_STATE_KEYS);
}

/** Sanitize the `buildingsSummary/{key}` node for export (same state stripping, no subtree exclusions). */
export function sanitizeSummaryForExport(summary: unknown): unknown {
    return stripKeysDeep(stripDerivedUrls(summary), STRIPPED_STATE_KEYS);
}

/**
 * Collect every `storagePath` string in a subtree. Used to warn about
 * DB-referenced files that live outside the building's Storage prefix
 * (those are not bundled).
 */
export function collectStoragePaths(...roots: unknown[]): string[] {
    const paths = new Set<string>();

    const visit = (value: unknown): void => {
        if (value === null || typeof value !== 'object') {
            return;
        }
        if (Array.isArray(value)) {
            for (const item of value) visit(item);
            return;
        }
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
            if (key === 'storagePath' && typeof child === 'string' && child.length > 0) {
                paths.add(child);
            }
            visit(child);
        }
    };

    for (const root of roots) visit(root);
    return [...paths];
}

export interface ExportBinaryPaths {
    /** De-duplicated Storage paths to bundle, all under the allowed prefixes. */
    included: string[];
    /** DB-referenced paths outside the building's Storage areas — not bundled. */
    skipped: string[];
}

/**
 * Derive the list of Storage objects to export from the DB metadata itself:
 * every `storagePath` field plus the two document conventions, which store a
 * `storageFilename` and rebuild the path (see buildingDocumentsApi /
 * componentDocumentsApi). No Storage list call is made — the instances run
 * rules_version 1, where list operations are unsupported — and soft-deleted
 * orphan blobs are naturally left behind. Thumbnail derivatives are never
 * referenced by these fields; the defensive filter keeps a crafted path out.
 */
export function collectExportBinaryPaths(
    building: unknown,
    summary: unknown,
    buildingKey: string
): ExportBinaryPaths {
    const paths = new Set<string>(collectStoragePaths(building, summary));

    const addDocumentPaths = (documents: unknown, basePath: string): void => {
        if (!documents || typeof documents !== 'object' || Array.isArray(documents)) return;
        for (const document of Object.values(documents as Record<string, unknown>)) {
            if (!document || typeof document !== 'object') continue;
            const storageFilename = (document as { storageFilename?: unknown }).storageFilename;
            if (typeof storageFilename === 'string' && storageFilename.length > 0) {
                paths.add(`${basePath}/${storageFilename}`);
            }
        }
    };

    const buildingRecord =
        building && typeof building === 'object' ? (building as Record<string, unknown>) : {};
    addDocumentPaths(buildingRecord.documents, `buildings/${buildingKey}/documents`);
    const structure =
        buildingRecord.structure && typeof buildingRecord.structure === 'object'
            ? (buildingRecord.structure as Record<string, unknown>)
            : {};
    const components =
        structure.components && typeof structure.components === 'object'
            ? (structure.components as Record<string, unknown>)
            : {};
    for (const [componentKey, component] of Object.entries(components)) {
        if (!component || typeof component !== 'object') continue;
        addDocumentPaths(
            (component as Record<string, unknown>).documents,
            `buildings/${buildingKey}/structure/components/${componentKey}/documents`
        );
    }

    const allowedPrefixes = bundleStoragePrefixes(buildingKey);
    const included: string[] = [];
    const skipped: string[] = [];
    for (const path of paths) {
        if (path.includes('..') || isThumbnailDerivativePath(path)) {
            skipped.push(path);
        } else if (allowedPrefixes.some(prefix => path.startsWith(prefix))) {
            included.push(path);
        } else {
            skipped.push(path);
        }
    }
    return { included, skipped };
}

/** Count component nodes under a building model's structure. */
export function countComponents(building: unknown): number {
    const structure = (building as { structure?: { components?: Record<string, unknown> } } | null)
        ?.structure;
    const components = structure?.components;
    return components ? Object.keys(components).length : 0;
}
